import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { barterApi, settingsApi } from '@/services/api'
import { useNavigate, useParams } from 'react-router-dom'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import Alert from '@/components/ui/Alert'
import Loading from '@/components/ui/Loading'
import LocationSelect from '@/components/forms/LocationSelect'
import CategorySelect from '@/components/forms/CategorySelect'
import ImageUpload from '@/components/barters/ImageUpload'
import { useAuth } from '@/hooks/useAuth'

export default function BarterEdit() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    interested_in: '',
    allow_messages: true,
    type: 'goods',
    dollar_value: '',
    barter_coin_amount: '0',
    continent: '',
    country: '',
    state: '',
    city: '',
    visibility: 'active',
    status: 'open',
    categories: [] as string[],
  })
  const [error, setError] = useState('')

  // Load system settings for escrow percentage
  const { data: settingsData } = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.get(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  const escrowPercentage = parseFloat(settingsData?.data?.settings?.escrow_fee_percentage || '10') / 100

  const { data: barterData, isLoading } = useQuery({
    queryKey: ['barter', id],
    queryFn: () => barterApi.get(Number(id)),
    enabled: !!id,
  })

  const { data: imagesData } = useQuery({
    queryKey: ['barter-images', id],
    queryFn: async () => {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/barter-images?barter_id=${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
      return response.json()
    },
    enabled: !!id,
  })

  useEffect(() => {
    if (barterData?.data?.barter) {
      const barter = barterData.data.barter
      setFormData({
        title: barter.title || '',
        description: barter.description || '',
        interested_in: barter.interested_in || '',
        allow_messages: barter.allow_messages !== undefined ? barter.allow_messages : true,
        type: barter.type || 'goods',
        dollar_value: barter.dollar_value?.toString() || '',
        barter_coin_amount: barter.barter_coin_amount?.toString() || '0',
        continent: barter.continent || '',
        country: barter.country || '',
        state: barter.state || '',
        city: barter.city || '',
        visibility: barter.visibility || 'active',
        status: barter.status || 'open',
        categories: barter.categories || [],
      })
    }
  }, [barterData])

  const updateMutation = useMutation({
    mutationFn: (data: any) => barterApi.update(Number(id), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['barters'] })
      queryClient.invalidateQueries({ queryKey: ['barter', id] })
      navigate('/dashboard/my-barters')
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update barter')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (formData.categories.length === 0) {
      setError('Please select at least one category')
      return
    }

    const dollarValue = parseFloat(formData.dollar_value)
    if (dollarValue > 100000) {
      setError('Dollar value cannot exceed $100,000.00')
      return
    }

    updateMutation.mutate(formData)
  }

  // Check if barter is in a read-only status
  const isReadOnlyStatus = ['in_progress', 'completed', 'closed', 'conflict'].includes(
    formData.status
  )

  // Check if user can edit this barter (owner or super admin)
  const barter = barterData?.data?.barter
  const canEdit = user?.user_type === 'super_admin' || barter?.user_id === user?.id

  if (isLoading) return <Loading />

  // Show access denied if user cannot edit
  if (barter && !canEdit) {
    return (
      <div className="max-w-4xl mx-auto">
        <Alert
          type="error"
          message="You do not have permission to edit this barter. Only the owner or super admin can edit barters."
        />
        <div className="mt-4">
          <Button onClick={() => navigate('/dashboard/my-barters')}>Back to My Barters</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Edit Barter</h1>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      <form onSubmit={handleSubmit}>
        <Card>
          <div className="space-y-6">
            <div>
              <label className="label">Title *</label>
              <input
                type="text"
                className="input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                readOnly={isReadOnlyStatus}
                disabled={isReadOnlyStatus}
              />
            </div>

            <div>
              <label className="label">Description *</label>
              <textarea
                className="textarea"
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                readOnly={isReadOnlyStatus}
                disabled={isReadOnlyStatus}
              />
            </div>

            <div>
              <label className="label">Interested In</label>
              <textarea
                className="textarea"
                rows={3}
                value={formData.interested_in}
                onChange={(e) => setFormData({ ...formData, interested_in: e.target.value })}
                placeholder="What are you looking for in exchange? (e.g., web design, landscaping, furniture, etc.)"
                readOnly={isReadOnlyStatus}
                disabled={isReadOnlyStatus}
              />
              <p className="text-xs text-gray-500 mt-1">
                Help others know what you'd like to trade for
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="label">Type *</label>
                <select
                  className="select"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                  disabled={isReadOnlyStatus}
                >
                  <option value="goods">Goods</option>
                  <option value="services">Services</option>
                </select>
              </div>

              <div>
                <label className="label">Dollar Value *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100000"
                  className="input"
                  value={formData.dollar_value}
                  onChange={(e) => setFormData({ ...formData, dollar_value: e.target.value })}
                  required
                  readOnly={isReadOnlyStatus}
                  disabled={isReadOnlyStatus}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {(() => {
                    const dollarValue = parseFloat(formData.dollar_value) || 0
                    const originalValue = parseFloat(String(barterData?.data?.barter?.dollar_value || '0'))
                    if (dollarValue < 100) {
                      return '0% escrow required (0.00 coins). Maximum: $100,000.00'
                    }
                    const escrowAmount = (dollarValue * escrowPercentage).toFixed(2)
                    const escrowPercent = (escrowPercentage * 100).toFixed(0)
                    const msg = `${escrowPercent}% escrow required (${escrowAmount} coins). Maximum: $100,000.00`
                    // Warn if increasing from < 100 to >= 100
                    if (originalValue < 100 && dollarValue >= 100) {
                      return <span className="text-amber-600">{msg} - Escrow will be collected upon save</span>
                    }
                    return msg
                  })()}
                </p>
              </div>

              <div>
                <label className="label">Barter Coins</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="input"
                  value={formData.barter_coin_amount}
                  onChange={(e) => setFormData({ ...formData, barter_coin_amount: e.target.value })}
                />
                {isReadOnlyStatus && (
                  <p className="text-xs text-amber-600 mt-1">
                    This field can still be edited
                  </p>
                )}
              </div>
            </div>

            {isReadOnlyStatus && (
              <Alert
                type="warning"
                message="This barter is in a locked status. Most fields cannot be edited except Barter Coins, Visibility, Status, and Private Messaging settings."
              />
            )}

            <CategorySelect
              selected={formData.categories}
              onChange={(categories) => setFormData({ ...formData, categories })}
              required
              disabled={isReadOnlyStatus}
            />

            <LocationSelect
              continent={formData.continent}
              country={formData.country}
              state={formData.state}
              city={formData.city}
              onContinentChange={(continent) =>
                setFormData({ ...formData, continent, country: '', state: '', city: '' })
              }
              onCountryChange={(country) =>
                setFormData({ ...formData, country, state: '', city: '' })
              }
              onStateChange={(state) =>
                setFormData({ ...formData, state, city: '' })
              }
              onCityChange={(city) => setFormData({ ...formData, city })}
              disabled={isReadOnlyStatus}
            />

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="label">Visibility</label>
                <select
                  className="select"
                  value={formData.visibility}
                  onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div>
                <label className="label">Status</label>
                <select
                  className="select"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="open">Open</option>
                  <option value="ready">Ready</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="closed">Closed</option>
                  <option value="conflict">Conflict</option>
                </select>
              </div>

              <div>
                <label className="label">Private Messaging</label>
                <select
                  className="select"
                  value={formData.allow_messages ? 'on' : 'off'}
                  onChange={(e) => setFormData({ ...formData, allow_messages: e.target.value === 'on' })}
                >
                  <option value="on">On (Allow messages)</option>
                  <option value="off">Off (Disable messages)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Allow users to send you private messages about this barter
                </p>
              </div>
            </div>
          </div>
        </Card>

        <div className="mt-6 flex justify-end space-x-4">
          <Button type="button" variant="secondary" onClick={() => navigate('/dashboard/my-barters')}>
            Cancel
          </Button>
          <Button type="submit" isLoading={updateMutation.isPending}>
            Update Barter
          </Button>
        </div>
      </form>

      {/* Image Upload Section - Outside form for independent operation */}
      <Card title="Barter Images" className="mt-6">
        <ImageUpload
          barterId={Number(id)}
          images={imagesData?.data?.images || []}
          maxImages={25}
          canEdit={canEdit}
          onImagesChange={() => {
            // Refresh barter images after upload/delete
            queryClient.invalidateQueries({ queryKey: ['barter-images', id] })
            queryClient.invalidateQueries({ queryKey: ['barter', id] })
          }}
        />
      </Card>
    </div>
  )
}
